/**
 * Shared helpers for the three "system" identity questions (first name, last
 * name, email) that collect ADDITIONAL guests (Guest 2+) at checkout.
 *
 * The buyer (Guest 1 / guest index 0) is always collected earlier in the flow
 * (the personal-details step, or downstream at the basket checkout page), so a
 * question carrying a `systemRole` renders only for guest index >= 1. A listing
 * "collects additional guests via questions" when its booking form contains
 * these questions — added by the API backfill for listings that don't prefill
 * other guests. See the API's `systemGuestQuestions.js` for the write side.
 *
 * Kept as a structural helper (not tied to a specific `Question` type) because
 * the checkout uses a couple of slightly different question shapes.
 */

export const SYSTEM_GUEST_ROLES = ["firstName", "lastName", "email"] as const;
export type SystemGuestRole = (typeof SYSTEM_GUEST_ROLES)[number];

type GuestQuestion = {
  systemRole?: string | null;
  isPerGuest?: boolean;
  isRequired?: boolean;
};

export function isSystemGuestRole(
  role: string | null | undefined,
): role is SystemGuestRole {
  return role != null && (SYSTEM_GUEST_ROLES as readonly string[]).includes(role);
}

/**
 * The first guest index a per-guest question collects for. System identity
 * questions skip the buyer (index 0); every other per-guest question starts at 0.
 */
export function firstGuestIndexFor(question: GuestQuestion): number {
  return isSystemGuestRole(question.systemRole) ? 1 : 0;
}

/**
 * Whether a question renders at least one input given the guest count.
 *
 * System identity questions render only when the flow is collecting additional
 * guests via the booking form (`collectSystemGuests`, i.e. the basket path where
 * the personal-details step is skipped) AND there is at least one additional
 * guest (totalGuests > 1). When the personal step still collects guests, or on a
 * single-seat booking, they render nothing. Non-per-guest questions always
 * render; other per-guest questions render whenever there is a guest.
 */
export function isQuestionRenderable(
  question: GuestQuestion,
  totalGuests: number,
  collectSystemGuests: boolean = false,
  participantsEnabled: boolean = false,
): boolean {
  if (!question.isPerGuest) return true;
  if (isSystemGuestRole(question.systemRole)) {
    // Participants replace the system guest-identity questions — when enabled,
    // identity is collected by the participants step, so these never render
    // (the owner's per-guest custom questions still do).
    if (participantsEnabled) return false;
    return collectSystemGuests && totalGuests > 1;
  }
  return totalGuests > 0;
}

/**
 * True when the booking form carries the system identity questions — i.e. this
 * listing collects additional-guest details via the userform (a backfilled
 * listing) rather than the legacy personal-details step.
 */
export function hasSystemGuestQuestions(
  questions: GuestQuestion[] | undefined | null,
): boolean {
  return !!questions?.some((q) => isSystemGuestRole(q.systemRole));
}

/**
 * Resolve how a checkout flow collects guests for a listing (shared by the
 * regular/term/trial hooks so the rule lives in one place):
 *
 * - `collectsGuestsViaQuestions`: the enabled booking form carries the system
 *   identity questions, so additional guests come from the userform.
 * - `skipPersonalStep`: every flow is a basket flow, so the in-flow
 *   personal-details step never renders — the buyer is collected at the basket
 *   checkout page, and additional guests come from the identity questions when
 *   present, or aren't collected at all (the owner's choice, expressed by
 *   deleting the questions).
 *
 * The `allowPrefillOthersGuestsOnCheckout` course flag is frozen and
 * deliberately NOT consulted: question presence is the single source of truth.
 */
export function resolveGuestCollectionMode(
  questions: GuestQuestion[] | undefined | null,
  opts: {
    formEnabled?: boolean;
  },
): {
  collectsGuestsViaQuestions: boolean;
  skipPersonalStep: boolean;
  collectSystemGuests: boolean;
} {
  const collectsGuestsViaQuestions =
    !!opts.formEnabled && hasSystemGuestQuestions(questions);
  // Every flow is a basket flow now: the buyer is always collected at the
  // basket checkout page, so the in-flow personal step never renders.
  return {
    collectsGuestsViaQuestions,
    skipPersonalStep: true,
    collectSystemGuests: true,
  };
}

/** True if at least one question renders an input given the guest count. */
export function hasRenderableQuestions(
  questions: GuestQuestion[] | undefined | null,
  totalGuests: number,
  collectSystemGuests: boolean = false,
  participantsEnabled: boolean = false,
): boolean {
  return !!questions?.some((q) =>
    isQuestionRenderable(q, totalGuests, collectSystemGuests, participantsEnabled),
  );
}

/**
 * RENDER predicate: whether a listing's booking form presents ANY question
 * (required or optional) to the buyer for a given guest count — the form is
 * enabled AND at least one question renders an input. Use this to decide whether
 * to render the question section: optional questions must still show so the
 * buyer can answer them.
 *
 * Do NOT use this to gate a submit button — see
 * `formHasRenderableRequiredQuestions`. Gating on "any renderable question"
 * wrongly blocks a form of only-optional questions (and, in oneAtTime mode,
 * forces the buyer to page through every optional step before the button
 * unlocks).
 */
export function formHasRenderableQuestions(
  form:
    | { isEnabled?: boolean; questions?: GuestQuestion[] | null }
    | undefined
    | null,
  totalGuests: number,
  collectSystemGuests: boolean = false,
  participantsEnabled: boolean = false,
): boolean {
  if (!form?.isEnabled) return false;
  return hasRenderableQuestions(
    form.questions,
    totalGuests,
    collectSystemGuests,
    participantsEnabled,
  );
}

/** True if at least one REQUIRED question renders an input given the guest count. */
export function hasRenderableRequiredQuestions(
  questions: GuestQuestion[] | undefined | null,
  totalGuests: number,
  collectSystemGuests: boolean = false,
  participantsEnabled: boolean = false,
): boolean {
  return !!questions?.some(
    (q) =>
      q.isRequired &&
      isQuestionRenderable(
        q,
        totalGuests,
        collectSystemGuests,
        participantsEnabled,
      ),
  );
}

/**
 * BUTTON-GATE predicate: whether a form must block a "complete your questions"
 * submit button for a given guest count — the form is enabled AND at least one
 * REQUIRED question actually renders an input.
 *
 * This is the precise middle ground between the two ways to get it wrong:
 * - gating on raw `some(q => q.isRequired)` counts the hidden system identity
 *   questions (each stamped `isRequired` by the backfill) that render nothing,
 *   so the button locks on "Complete required questions" with no questions shown;
 * - gating on "any renderable question" blocks a form of only-optional questions.
 * Requiring BOTH renderable AND required avoids both. Pair it with
 * `formHasRenderableQuestions` for the render decision.
 */
export function formHasRenderableRequiredQuestions(
  form:
    | { isEnabled?: boolean; questions?: GuestQuestion[] | null }
    | undefined
    | null,
  totalGuests: number,
  collectSystemGuests: boolean = false,
  participantsEnabled: boolean = false,
): boolean {
  if (!form?.isEnabled) return false;
  return hasRenderableRequiredQuestions(
    form.questions,
    totalGuests,
    collectSystemGuests,
    participantsEnabled,
  );
}
